import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowUpRight, Lock } from 'lucide-react';
import StoreLink from './ui/StoreLink';
import Reveal from './ui/Reveal';

type Category = 'healthcare' | 'booking' | 'marketplace' | 'fintech' | 'web';

type Store = {
  kind: 'play' | 'appstore';
  href: string;
  /**
   * Names app and destination, and — critically — must *contain* the button's
   * visible text. WCAG 2.5.3 Label in Name: "View Rezma on Google Play" does not
   * contain "View on Google Play", so a speech-input user asking for the visible
   * label got no match. Lighthouse flagged it as label-content-name-mismatch.
   */
  ariaLabel: string;
};

interface Project {
  id: string;
  category: Category;
  image: string;
  technologies: string[];
  liveUrl: string;
  featured?: boolean;
  /** Present only for apps that are actually published in a store. Rezma ships
   *  on both, so this is a list: one full-width button per storefront. */
  stores?: Store[];
  /** True when liveUrl points at the same place as a store link. */
  liveIsStore?: boolean;
}

// Categories are real and mutually exclusive so every filter returns results.
const projects: Project[] = [
  {
    id: 'medical',
    category: 'healthcare',
    image: '/projects/medical.png',
    technologies: ['Flutter', 'BLoC', '.NET', 'RESTful APIs'],
    // QUESTIONS.md #1, resolved: not a bug after all. This URL was removed
    // because the listing is titled "نظام rezma..." and so looked like a
    // mis-paste of the Rezma link — but Anas confirmed it. The clinics system
    // shipped under the Rezma brand, and 3655835 (clinics / patient records /
    // bookings) is its own listing, distinct from Rezma's 3350933 (restaurants /
    // hotels / clinics / farms / cafes). The CV's missing `live:` line was the
    // omission, not the link.
    liveUrl:
      'https://mostaql.com/portfolio/3655835-%D9%86%D8%B8%D8%A7%D9%85-rezma-%D9%84%D8%A5%D8%AF%D8%A7%D8%B1%D8%A9-%D8%A7%D9%84%D8%B9%D9%8A%D8%A7%D8%AF%D8%A7%D8%AA-%D9%88%D9%85%D9%84%D9%81%D8%A7%D8%AA-%D8%A7%D9%84%D9%85%D8%B1%D8%B6%D9%89-%D9%88%D8%A7%D9%84%D8%AD%D8%AC%D9%88%D8%B2%D8%A7%D8%AA',
    featured: true,
  },
  {
    id: 'rezma',
    category: 'booking',
    image: '/projects/rezma.png',
    technologies: ['Flutter', '.NET', 'RESTful APIs'],
    liveUrl:
      'https://mostaql.com/portfolio/3350933-%D9%86%D8%B8%D8%A7%D9%85-rezma-%D9%84%D8%AD%D8%AC%D8%B2-%D8%A7%D9%84%D9%85%D8%B7%D8%A7%D8%B9%D9%85-%D9%81%D9%86%D8%A7%D8%AF%D9%82-%D8%B9%D9%8A%D8%A7%D8%AF%D8%A7%D8%AA-%D9%85%D8%B2%D8%A7%D8%B1%D8%B9%D9%85%D9%82%D8%A7%D9%87%D9%8A-%D9%85%D8%B9-%D9%84%D9%88%D8%AD%D8%A9-%D8%AA%D8%AD%D9%83%D9%85',
    featured: true,
    stores: [
      {
        kind: 'play',
        href: 'https://play.google.com/store/apps/details?id=com.rezma.app',
        ariaLabel: 'View on Google Play — Rezma',
      },
      {
        kind: 'appstore',
        // Region-neutral form, same convention as the Play link - verified 200.
        href: 'https://apps.apple.com/app/id6798466295',
        ariaLabel: 'View on App Store — Rezma',
      },
    ],
  },
  {
    id: 'homez24',
    category: 'marketplace',
    image: '/projects/homez24.png',
    technologies: ['Flutter', 'BLoC', 'Firebase'],
    liveUrl:
      'https://mostaql.com/portfolio/939540-%D8%AA%D8%B7%D8%A8%D9%8A%D9%82-homez24-%D9%84%D8%A8%D9%8A%D8%B9-%D9%88%D8%A7%D8%B3%D8%A6%D8%AC%D8%A7%D8%B1-%D9%88%D8%B4%D8%B1%D8%A7%D8%A1-%D8%A7%D9%84%D8%B9%D9%82%D8%A7%D8%B1%D8%A7%D8%AA',
    featured: true,
  },
  {
    id: 'scopepadel',
    category: 'booking',
    image: '/projects/scopepadel.png',
    technologies: ['Flutter', 'Firebase', 'Stripe'],
    liveUrl:
      'https://mostaql.com/portfolio/803076-%D8%AA%D8%B7%D8%A8%D9%8A%D9%82-scope-padel-%D9%84%D9%84%D8%A7%D8%B9%D8%A8%D9%8A%D9%86-%D9%88%D8%A3%D8%B5%D8%AD%D8%A7%D8%A8-%D9%85%D9%84%D8%A7%D8%B9%D8%A8-%D8%A7%D9%84%D8%A8%D8%A7%D8%AF%D9%84-%D8%AA%D9%86%D8%B3-%D9%81%D9%8A-%D8%A7%D9%84%D8%A7%D9%85%D8%A7%D8%B1%D8%A7%D8%AA-iosandroid',
  },
  {
    id: 'wjhtak',
    category: 'booking',
    image: '/projects/wjhtak.png',
    technologies: ['Flutter', 'REST APIs', 'PayPal'],
    // Region-neutral form — verified 200, auto-localises to the visitor's storefront.
    liveUrl: 'https://apps.apple.com/app/id6590636750',
  },
  {
    id: 'firstpay',
    category: 'fintech',
    image: '/projects/firstpay.png',
    technologies: ['Flutter', 'BLoC', 'Laravel', 'RESTful APIs'],
    liveUrl: '#', // private / NDA project — no public link
  },
  {
    id: '4you',
    category: 'fintech',
    image: '/projects/4you.png',
    technologies: ['Flutter', 'Firebase'],
    liveUrl:
      'https://mostaql.com/portfolio/2690822-%D8%AA%D8%B7%D8%A8%D9%8A%D9%82-%D8%AF%D9%81%D8%B9-%D8%A7%D9%84%D9%83%D8%AA%D8%B1%D9%88%D9%86%D9%8A-%D9%84%D9%84%D9%81%D9%88%D8%A7%D8%AA%D9%8A%D8%B1-%D9%88%D8%AA%D8%B9%D8%A8%D8%A6%D8%A9-%D9%88%D8%B4%D8%AD%D9%86-%D8%B1%D8%B5%D9%8A%D8%AF-%D9%88%D8%A8%D8%A7%D9%82%D8%A7%D8%AA-%D9%84%D9%84%D8%B4%D8%B1%D9%83%D8%A7%D8%AA-%D9%88%D8%A7%D9%84%D8%B9%D9%85%D9%84%D8%A7%D8%A1-%D9%85%D8%B9-%D9%84%D9%88%D8%AD%D8%A9-%D8%AA%D8%AD%D9%83%D9%85',
  },
  {
    id: 'manjam',
    category: 'marketplace',
    image: '/projects/manjam.png',
    technologies: ['Flutter', 'Firebase', 'Provider'],
    liveUrl: 'https://play.google.com/store/apps/details?id=com.manjam.app',
  },
  {
    id: 'dashboard',
    category: 'web',
    image: '/projects/dashboard.png',
    technologies: ['Flutter Web', '.NET'],
    liveUrl: 'https://resoo-app.com',
  },
];

/**
 * Six of the nine assets are client logos drawn for a white ground, so the media
 * area is a light plate in both themes rather than a tinted tile.
 *
 * The missing-asset state is designed, not accidental: the project's own title set
 * as a letterspaced wordmark on the same plate. It reads as a placeholder mark
 * rather than a broken image.
 */
/**
 * Basenames that have a .webp sibling on disk.
 *
 * This list is load-bearing, not an optimisation detail. <picture> picks a
 * <source> by type and does NOT fall through to the <img> if that source 404s —
 * so emitting a .webp source for a file that has no .webp would break an image
 * whose .png is present and perfectly good. Dropping medical.png into
 * public/projects is therefore enough on its own; add the id here only once a
 * matching medical.webp exists too.
 */
const HAS_WEBP = new Set(['rezma', 'wjhtak', 'homez24', 'manjam', 'firstpay', '4you']);

const ProjectMedia: React.FC<{
  src: string;
  alt: string;
  tall?: boolean;
  /** Fills the row height instead of holding an aspect ratio — the wide card. */
  fill?: boolean;
}> = ({ src, alt, tall, fill }) => {
  const [failed, setFailed] = useState(false);
  const base = src.replace(/^.*\//, '').replace(/\.png$/, '');
  const webp = HAS_WEBP.has(base) ? `/projects/${base}.webp` : null;

  return (
    <div
      className={`relative flex items-center justify-center overflow-hidden bg-plate ${
        fill
          ? 'aspect-[16/9] md:aspect-auto md:h-full md:min-h-[260px]'
          : tall
            ? 'aspect-[16/10]'
            : 'aspect-[4/3]'
      }`}
    >
      {failed ? (
        /* Missing-asset state, and a shipping one — three of the nine assets may
           never arrive. Set as a nameplate rather than a lone word: a rule, the
           title as a letterspaced wordmark, a rule. Scales with the tier, because
           at the featured size a single small word floating in a large plate reads
           as empty rather than as a decision. */
        <span className="flex w-full flex-col items-center gap-4 px-6" aria-hidden="true">
          <span className={`h-px bg-hairline ${tall ? 'w-16' : 'w-10'}`} />
          <span
            className={`ui text-center font-semibold uppercase leading-snug tracking-[0.22em] text-graphite ${
              tall ? 'text-[19px]' : 'text-[13px]'
            }`}
          >
            {alt}
          </span>
          <span className={`h-px bg-hairline ${tall ? 'w-16' : 'w-10'}`} />
        </span>
      ) : (
        <picture>
          {webp && <source srcSet={webp} type="image/webp" />}
          <img
            src={src}
            alt={alt}
            loading="lazy"
            decoding="async"
            onError={() => setFailed(true)}
            className={`h-full w-full object-contain transition-transform duration-500 ease-out group-hover:scale-[1.04] ${
              tall ? 'p-10' : 'p-7'
            }`}
          />
        </picture>
      )}
    </div>
  );
};

const Projects: React.FC = () => {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<'all' | Category>('all');

  const filters: { id: 'all' | Category; label: string }[] = [
    { id: 'all', label: t('projects.filters.all') },
    { id: 'healthcare', label: t('projects.filters.healthcare') },
    { id: 'booking', label: t('projects.filters.booking') },
    { id: 'marketplace', label: t('projects.filters.marketplace') },
    { id: 'fintech', label: t('projects.filters.fintech') },
    { id: 'web', label: t('projects.filters.web') },
  ];

  const visible = useMemo(
    () => (filter === 'all' ? projects : projects.filter((p) => p.category === filter)),
    [filter]
  );

  // Published apps outrank on size and position — 2-up above the rule against
  // 3-up below it — rather than on a badge. No copy carries the distinction.
  const shipped = visible.filter((p) => p.stores?.length);
  const rest = visible.filter((p) => !p.stores?.length);

  const meta = (project: Project) => (
    <div className="flex items-center gap-3">
      <span className="tag uppercase tracking-[0.16em]">
        {t(`projects.filters.${project.category}`)}
      </span>
      {project.featured && (
        <>
          <span aria-hidden="true" className="h-3 w-px bg-hairline" />
          <span className="tag uppercase tracking-[0.16em] text-lacquer-bright">
            {t('projects.featured')}
          </span>
        </>
      )}
    </div>
  );

  /* The card's own link, as a bounded control rather than bare text — see
     .btn-quiet. `aria-label` names the project because the visible label repeats
     across nine cards, and it *contains* that visible label so speech input still
     matches it (WCAG 2.5.3, the same rule StoreLink documents). */
  const secondaryLink = (project: Project) =>
    project.liveUrl === '#' ? (
      <span className="badge-quiet">
        <Lock size={14} aria-hidden="true" /> {t('projects.private')}
      </span>
    ) : (
      <a
        href={project.liveUrl}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={`${t('projects.viewProject')} — ${t(`projects.projects.${project.id}.title`)}`}
        className="btn-quiet"
      >
        {t('projects.viewProject')}
        <ArrowUpRight size={15} aria-hidden="true" />
      </a>
    );

  return (
    <section id="projects" className="section">
      <div className="container-page">
        <Reveal>
          <p className="eyebrow">{t('projects.eyebrow')}</p>
          <h2 className="section-title mt-4 max-w-2xl">{t('projects.title')}</h2>
          <p className="mt-5 max-w-prose text-graphite">{t('projects.subtitle')}</p>
        </Reveal>

        {/* Filters */}
        <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-1" role="group">
          {filters.map((f) => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              aria-pressed={filter === f.id}
              className={`ui relative flex min-h-[44px] min-w-[44px] items-center justify-center text-[12px] font-semibold uppercase tracking-[0.16em] transition-colors duration-200 ${
                filter === f.id ? 'text-ink' : 'text-graphite hover:text-ink'
              }`}
            >
              {f.label}
              {filter === f.id && (
                <span
                  aria-hidden="true"
                  className="absolute inset-x-0 bottom-2 h-px bg-lacquer-bright"
                />
              )}
            </button>
          ))}
        </div>

        {/* Published apps — larger, first, above the rule */}
        {shipped.length > 0 && (
          <Reveal className="mt-10 grid gap-6 md:grid-cols-2">
            {shipped.map((project) => {
              const title = t(`projects.projects.${project.id}.title`);
              return (
                <article
                  key={project.id}
                  className="card-lift group flex flex-col border border-hairline bg-plate"
                  style={{ borderRadius: 2 }}
                >
                  <ProjectMedia src={project.image} alt={title} tall />
                  <div className="flex flex-1 flex-col border-t border-hairline p-6 sm:p-7">
                    {meta(project)}
                    <h3 className="ui mt-3 text-[24px] font-bold leading-tight tracking-[-0.01em] text-ink">
                      {title}
                    </h3>
                    <p className="mt-3 text-[15px] leading-relaxed text-graphite">
                      {t(`projects.projects.${project.id}.description`)}
                    </p>
                    {/* Tags and the secondary link share a row so that BOTH cards in
                        the pair end on the same element: a full-width store button.
                        A card whose liveUrl is already one of its store links drops
                        the secondary link rather than repeating the URL, and hanging
                        that difference off the bottom left the pair looking lopsided
                        at 2-up. Balanced by layout, not by duplicating a URL. */}
                    <div className="mt-5 flex flex-wrap items-center justify-between gap-x-6 gap-y-2">
                      <ul className="flex flex-wrap gap-x-3 gap-y-1">
                        {project.technologies.map((tech) => (
                          <li key={tech} className="tag">
                            {tech}
                          </li>
                        ))}
                      </ul>
                      {!project.liveIsStore && secondaryLink(project)}
                    </div>

                    <div className="mt-auto flex flex-col gap-3 pt-6">
                      {project.stores!.map((store) => (
                        <StoreLink
                          key={store.kind}
                          store={store.kind}
                          href={store.href}
                          ariaLabel={store.ariaLabel}
                          label={
                            store.kind === 'play'
                              ? t('projects.store.googlePlay')
                              : t('projects.store.appStore')
                          }
                        />
                      ))}
                    </div>
                  </div>
                </article>
              );
            })}
          </Reveal>
        )}

        {shipped.length > 0 && rest.length > 0 && <hr className="rule mt-12" />}

        {/* Everything else.
            When the last row would leave a single orphan, that card spans the full
            row as a wide horizontal card instead. It is not a promotion — same
            tier, same type sizes, same links — only a different container, so the
            grid resolves as 2 large / 6 medium / 1 wide with a real bottom edge
            rather than a row that ran out.
            Below md everything is single-column, so this is a >=768 treatment. */}
        {rest.length > 0 && (
          <Reveal className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {rest.map((project, i) => {
              const title = t(`projects.projects.${project.id}.title`);
              const isWide = i === rest.length - 1 && rest.length > 3 && rest.length % 3 === 1;

              const body = (
                <>
                  {meta(project)}
                  <h3 className="ui mt-3 text-[18px] font-bold leading-tight text-ink">{title}</h3>
                  <p className="mt-2.5 text-[14px] leading-relaxed text-graphite">
                    {t(`projects.projects.${project.id}.description`)}
                  </p>
                  <ul className="mt-4 flex flex-wrap gap-x-3 gap-y-1">
                    {project.technologies.map((tech) => (
                      <li key={tech} className="tag">
                        {tech}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-auto pt-4">{secondaryLink(project)}</div>
                </>
              );

              if (isWide) {
                return (
                  <article
                    key={project.id}
                    className="card-lift group border border-hairline bg-plate md:col-span-2 lg:col-span-3"
                    style={{ borderRadius: 2 }}
                  >
                    <div className="md:grid md:grid-cols-3">
                      {/* object-contain throughout, so a real screenshot letterboxes
                          on the plate rather than stretching at this ratio. */}
                      <div className="border-b border-hairline md:col-span-2 md:border-b-0 md:border-r">
                        <ProjectMedia src={project.image} alt={title} fill />
                      </div>
                      <div className="flex flex-col p-5">{body}</div>
                    </div>
                  </article>
                );
              }

              return (
                <article
                  key={project.id}
                  className="card-lift group flex flex-col border border-hairline bg-plate"
                  style={{ borderRadius: 2 }}
                >
                  <ProjectMedia src={project.image} alt={title} />
                  <div className="flex flex-1 flex-col border-t border-hairline p-5">{body}</div>
                </article>
              );
            })}
          </Reveal>
        )}

        <Reveal className="mt-14">
          <hr className="rule" />
          <div className="flex flex-col items-start gap-5 pt-8 sm:flex-row sm:items-center sm:justify-between">
            <p className="max-w-prose text-graphite">{t('projects.ctaText')}</p>
            <a href="#contact" className="btn-primary group shrink-0">
              {t('projects.ctaButton')}
              <ArrowUpRight
                size={17}
                aria-hidden="true"
                className="transition-transform duration-200 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
              />
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default Projects;
